import { PaneBackButton } from "@/components/PaneBackButton";
import { PaneExpandButton } from "@/components/PaneExpandButton";
import {
  usePaneLocation,
  usePaneRouterContextSafe,
} from "@/components/PaneRouter";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAppNavigate } from "@/hooks/pane-routing";
import {
  CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS,
  CANVAS_DETAIL_PANE_HEADER_CLASS,
} from "@/panes/canvasDetailPaneHeaderClasses";
import { EntityPaneHeaderObjectActions } from "@/panes/shared/EntityPaneHeaderObjectActions";
import {
  IntegrationIcon,
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
} from "@cohesive/ui-components";
import { format } from "date-fns";
import { ExternalLink, X } from "lucide-react";
import { useCallback } from "react";
import { useLocation } from "react-router-dom";

function formatTimestamp(ts?: string): string | undefined {
  if (!ts) return undefined;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? undefined : format(d, "PPpp");
}

/**
 * Slack message detail. Slack message generic entities carry the message content
 * (title), author (subtitle), time, and permalink (url), but NOT the channel id —
 * a server re-fetch (channel + ts) isn't possible, so this renders straight from
 * the generic entity. Thread/reply enrichment is a future enhancement.
 */
export function SlackMessageDetailPane() {
  const appNavigate = useAppNavigate();
  const paneContext = usePaneRouterContextSafe();
  const paneLocation = usePaneLocation("rightPane");
  const mainLocation = useLocation();
  const entity = paneLocation?.state?.entity ?? mainLocation.state?.entity;
  const data = (entity?.data ?? {}) as Record<string, unknown>;

  const author = data.subtitle as string | undefined;
  const text =
    (data.title as string | undefined) ??
    (data.description as string | undefined);
  const when = formatTimestamp(data.timestamp as string | undefined);
  const url = data.url as string | undefined;
  const avatar = data.image_url as string | undefined;

  const handleClose = useCallback(() => {
    appNavigate({ pane: "rightPane", to: null });
  }, [appNavigate]);

  const handleOpenExternal = useCallback(() => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }, [url]);

  return (
    <PaneLayout>
      <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
        <PaneLayoutHeaderActions
          className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
        >
          <PaneBackButton />

          <div className="flex min-w-0 flex-1 items-center gap-1.5 pl-1">
            <IntegrationIcon
              platform="slack"
              name="Slack"
              className="h-4 w-4 shrink-0"
            />
            <span className="text-sm font-medium text-foreground truncate leading-none">
              Slack message
            </span>
          </div>

          <TooltipProvider>
            {entity && (
              <EntityPaneHeaderObjectActions
                entity={entity}
                name="Slack message"
              />
            )}

            <PaneExpandButton />

            {paneContext && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    className="shrink-0"
                    onClick={handleClose}
                    aria-label="Close"
                  >
                    <X />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </PaneLayoutHeaderActions>
      </PaneLayoutHeader>

      <PaneLayoutContent className="!p-0">
        <div className="flex flex-col gap-3 p-4">
          <div className="flex items-center gap-2.5">
            {avatar ? (
              <img
                src={avatar}
                alt=""
                className="h-9 w-9 rounded-md object-cover"
              />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
                <IntegrationIcon
                  platform="slack"
                  name="Slack"
                  className="h-4 w-4"
                />
              </div>
            )}
            <div className="min-w-0">
              {author && (
                <p className="text-sm font-semibold text-foreground truncate">
                  {author}
                </p>
              )}
              {when && <p className="text-xs text-muted-foreground">{when}</p>}
            </div>
          </div>

          {text ? (
            <p className="whitespace-pre-wrap break-words text-sm text-foreground leading-relaxed">
              {text}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">No message content.</p>
          )}

          {url && (
            <div>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={handleOpenExternal}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open in Slack
              </Button>
            </div>
          )}
        </div>
      </PaneLayoutContent>
    </PaneLayout>
  );
}
